import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ban,
  BookCheck,
  CalendarCheck,
  CheckCircle2,
  Download,
  Eye,
  FileCode2,
  HandCoins,
  Mail,
  Network,
  Plus,
  RotateCcw,
  Send,
  Undo2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingState } from '@/components/ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermission } from '@/components/rbac/use-permission';
import ZahlungslaufAblauf from '@/components/billing/ZahlungslaufAblauf';
import ZahlungslaeufeMonat from '@/components/billing/ZahlungslaeufeMonat';
import { P } from '@/lib/rbac/permissions.generated';
import {
  billingApi,
  type PaymentOrder,
  type PaymentRun,
  type PaymentRunDetail,
  type PaymentRunList,
  type PreNotificationResult,
  type RefundRequest,
} from '@/lib/api/billing';
import {
  BANK_STATUS,
  ORDER_STATUS,
  PROVIDER_LABEL,
  REFUND_STATUS,
  RETURN_REASONS,
  RUN_STATUS,
  formatCents,
  formatIsoDate,
  parseEuroToCents,
  todayIso,
} from '@/lib/billing';

const FINANZEN = P.buchhaltung.finanzdaten_und_zahlungen;
const ALL = 'all';
const HAUSBANK = 'hausbank_sepa';

type Confirmation = { kind: 'submit' | 'cancel' | 'upload' | 'transmit'; run: PaymentRunDetail } | null;

const isFileRun = (run: PaymentRun) => run.provider === HAUSBANK;

function preNotificationMessage(result: PreNotificationResult | null | undefined): string | null {
  if (!result) return null;
  const parts = [`${result.sent} Vorabinformation${result.sent === 1 ? '' : 'en'} versendet`];
  if (result.missingEmail) parts.push(`${result.missingEmail} ohne E-Mail-Adresse`);
  if (result.failed) parts.push(`${result.failed} fehlgeschlagen`);
  return parts.join(', ');
}

async function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function PaymentRunsPage() {
  const canEdit = usePermission(FINANZEN.zahlungen_bearbeiten);
  const canCapture = usePermission(FINANZEN.zahlung_erfassen);
  const canReturn = usePermission(P.buchhaltung.ruecklastschriften_und_mahnwesen.ruecklastschriften_bearbeiten);
  const canPostFees = usePermission(FINANZEN.offene_posten_bearbeiten);
  const canRefund = usePermission(FINANZEN.zahlung_zurueckerstatten);
  const [chargingFees, setChargingFees] = useState(false);
  const [refundsOpen, setRefundsOpen] = useState(false);

  const [list, setList] = useState<PaymentRunList>({ items: [], providers: [], entities: [] });
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<PaymentRunDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [returning, setReturning] = useState<PaymentOrder | null>(null);
  // Liste oder Monat im Kalender (T-0134); der Zähler lädt den Monat nach jeder Aktion neu.
  const [ansicht, setAnsicht] = useState<'liste' | 'monat'>('liste');
  const [monatNeuLaden, setMonatNeuLaden] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await billingApi.listRuns({ status: statusFilter === ALL ? undefined : statusFilter });
      setList({
        items: Array.isArray(response?.items) ? response.items : [],
        providers: Array.isArray(response?.providers) ? response.providers : [],
        entities: Array.isArray(response?.entities) ? response.entities : [],
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Zahlungsläufe konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  // Nach jeder Aktion lädt `load` die Liste neu — die Monatsansicht zieht mit.
  useEffect(() => {
    setMonatNeuLaden((n) => n + 1);
  }, [list]);

  const entityName = useMemo(() => new Map(list.entities.map((e) => [e.id, e.name])), [list.entities]);

  const openRun = async (run: PaymentRun) => {
    try {
      setDetail(await billingApi.getRun(run.id));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Zahlungslauf konnte nicht geladen werden');
    }
  };

  const act = async (action: () => Promise<PaymentRunDetail | PaymentOrder>, message: string) => {
    if (!detail) return;
    setBusy(true);
    try {
      const result = await action();
      const notices = preNotificationMessage((result as PaymentRunDetail).preNotifications);
      toast.success(notices ? `${message} · ${notices}` : message);
      setDetail(await billingApi.getRun(detail.id));
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
      setConfirmation(null);
    }
  };

  const download = async (run: PaymentRunDetail) => {
    try {
      const { blob, filename } = await billingApi.downloadRunFile(run.id);
      await saveBlob(blob, filename);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Datei konnte nicht geladen werden');
    }
  };

  const settleDueRuns = async () => {
    setBusy(true);
    try {
      const summary = await billingApi.runSepaSettle();
      toast.success(
        summary.settledOrders > 0
          ? `${summary.settledOrders} Lastschrift${summary.settledOrders === 1 ? '' : 'en'} als bezahlt gebucht`
          : 'Keine fälligen Einzüge offen',
      );
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Einzüge konnten nicht verbucht werden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Zahlungsläufe</h1>
        <p className="text-muted-foreground">
          Fällige offene Posten je abrechnender Einheit bündeln, freigeben und beim Zahlungsanbieter einreichen
          oder als Bankdatei exportieren.
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-52" aria-label="Status filtern">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Alle Status</SelectItem>
              {Object.entries(RUN_STATUS).map(([value, status]) => (
                <SelectItem key={value} value={value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Ansicht">
            {(['liste', 'monat'] as const).map((wert) => (
              <Button
                key={wert}
                size="sm"
                variant={ansicht === wert ? 'default' : 'ghost'}
                aria-pressed={ansicht === wert}
                onClick={() => setAnsicht(wert)}
              >
                {wert === 'liste' ? 'Liste' : 'Monat'}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canPostFees && (
            <Button variant="outline" onClick={() => setChargingFees(true)}>
              <CalendarCheck className="mr-2 h-4 w-4" />
              Beiträge buchen
            </Button>
          )}
          {canEdit && list.providers.includes(HAUSBANK) && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => void settleDueRuns()}
              title="Eingereichte Lastschriften bis heute als bezahlt buchen – läuft auch nachts automatisch"
            >
              <BookCheck className="mr-2 h-4 w-4" />
              Einzüge verbuchen
            </Button>
          )}
          {canRefund && (
            <Button variant="outline" onClick={() => setRefundsOpen(true)}>
              <HandCoins className="mr-2 h-4 w-4" />
              Erstattungen
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => setCreating(true)} disabled={list.entities.length === 0}>
              <Plus className="mr-2 h-4 w-4" />
              Zahlungslauf anlegen
            </Button>
          )}
        </div>
      </div>

      {ansicht === 'monat' ? (
        <ZahlungslaeufeMonat
          status={statusFilter === ALL ? undefined : statusFilter}
          neuLaden={monatNeuLaden}
          onOeffnen={(run) => void openRun(run)}
        />
      ) : loading ? (
        <LoadingState message="Zahlungsläufe werden geladen …" />
      ) : list.items.length === 0 ? (
        <EmptyState message="Noch keine Zahlungsläufe." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lauf</TableHead>
                <TableHead>Einheit</TableHead>
                <TableHead>Anbieter</TableHead>
                <TableHead>Termin</TableHead>
                <TableHead className="text-right">Aufträge</TableHead>
                <TableHead className="text-right">Summe</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.items.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="font-medium tabular-nums">
                    {run.runNumber}
                    {run.direction === 'credit' && (
                      <Badge variant="secondary" size="sm" className="ml-2">
                        Überweisung
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{entityName.get(run.billingEntityId) ?? `#${run.billingEntityId}`}</TableCell>
                  <TableCell>{PROVIDER_LABEL[run.provider] ?? run.provider}</TableCell>
                  <TableCell className="tabular-nums">{formatIsoDate(run.collectionDate)}</TableCell>
                  <TableCell className="text-right tabular-nums">{run.itemCount}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCents(run.totalCents)}</TableCell>
                  <TableCell>
                    <Badge variant={RUN_STATUS[run.status]?.variant ?? 'outline'} size="sm">
                      {RUN_STATUS[run.status]?.label ?? run.status}
                    </Badge>
                    {run.bankStatus && (
                      <Badge variant={BANK_STATUS[run.bankStatus]?.variant ?? 'outline'} size="sm" className="ml-1">
                        {BANK_STATUS[run.bankStatus]?.label ?? run.bankStatus}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => void openRun(run)}>
                      <Eye className="mr-2 h-4 w-4" />
                      Öffnen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {chargingFees && <ChargeFeesDialog onClose={() => setChargingFees(false)} />}

      {refundsOpen && (
        <RefundsDialog
          list={list}
          onClose={() => setRefundsOpen(false)}
          onRunCreated={async (run) => {
            setRefundsOpen(false);
            setDetail(run);
            await load();
          }}
        />
      )}

      {creating && (
        <CreateRunDialog
          list={list}
          onClose={() => setCreating(false)}
          onCreated={async (run) => {
            setCreating(false);
            setDetail(run);
            await load();
          }}
        />
      )}

      {detail && (
        <Dialog open onOpenChange={(open) => !open && setDetail(null)}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-3">
                Zahlungslauf {detail.runNumber}
                <Badge variant={RUN_STATUS[detail.status]?.variant ?? 'outline'} size="sm">
                  {RUN_STATUS[detail.status]?.label ?? detail.status}
                </Badge>
              </DialogTitle>
              <DialogDescription>
                {entityName.get(detail.billingEntityId) ?? `Einheit #${detail.billingEntityId}`} ·{' '}
                {PROVIDER_LABEL[detail.provider] ?? detail.provider} ·{' '}
                {detail.direction === 'credit' ? 'Ausführung' : 'Einzug'} am {formatIsoDate(detail.collectionDate)} ·{' '}
                {detail.itemCount} Aufträge · {formatCents(detail.totalCents)}
              </DialogDescription>
            </DialogHeader>

            {/* Der Lauf als Ablauf mit Schritten und Hinweis (T-0138). */}
            <ZahlungslaufAblauf run={detail} />

            {detail.bankStatus && (
              <div
                className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm ${detail.bankStatus === 'rejected' ? 'border-destructive/40 bg-destructive/10' : 'border-info/40 bg-info/10'}`}
              >
                <span>
                  <strong>{BANK_STATUS[detail.bankStatus]?.label}</strong> · OrderID {detail.bankOrderId ?? '–'}
                  {detail.bankStatus === 'transmitted' &&
                    ' – bitte den Auftrag in der Banking-App freigeben (verteilte Unterschrift).'}
                  {detail.bankStatus === 'rejected' &&
                    ' – laut Kundenprotokoll nicht ausgeführt. Der Lauf wird nicht als bezahlt gebucht.'}
                </span>
                {detail.bankStatus === 'rejected' && canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => void act(() => billingApi.resetTransmission(detail.id), 'Lauf zurückgesetzt')}
                  >
                    Übertragung zurücksetzen
                  </Button>
                )}
              </div>
            )}

            {isFileRun(detail) && detail.status === 'exported' && !detail.ebicsReady && (
              <div className="rounded-lg border border-info/40 bg-info/10 p-3 text-sm">
                <p className="font-medium">So geht es weiter</p>
                <ol className="mt-1 list-decimal space-y-0.5 pl-5">
                  <li>Bankdatei herunterladen.</li>
                  <li>
                    Im Onlinebanking als{' '}
                    {detail.direction === 'credit' ? 'SEPA-Sammelüberweisung' : 'SEPA-Sammellastschrift (pain.008)'}{' '}
                    hochladen und freigeben.
                  </li>
                  <li>Hier „Upload bestätigen“ – erst dann gilt der Lauf als eingereicht.</li>
                </ol>
                {detail.fileSha256 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {detail.fileName} · Prüfsumme {detail.fileSha256.slice(0, 16)}…
                  </p>
                )}
              </div>
            )}

            {canEdit && isFileRun(detail) && ['approved', 'exported', 'submitted', 'settled'].includes(detail.status) && (
              <div className="flex flex-wrap justify-end gap-2">
                {detail.status === 'approved' && detail.direction === 'debit' && (
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      void act(() => billingApi.sendPreNotifications(detail.id), 'Vorabinformationen geprüft')
                    }
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Vorabinformation nachsenden
                  </Button>
                )}
                {detail.status === 'exported' && (
                  <Button
                    variant="outline"
                    className="text-destructive"
                    disabled={busy}
                    onClick={() => setConfirmation({ kind: 'cancel', run: detail })}
                  >
                    <Ban className="mr-2 h-4 w-4" />
                    Lauf abbrechen
                  </Button>
                )}
                {detail.status === 'approved' && (
                  <Button disabled={busy} onClick={() => void act(() => billingApi.exportRun(detail.id), 'Bankdatei erzeugt')}>
                    <FileCode2 className="mr-2 h-4 w-4" />
                    Bankdatei erzeugen
                  </Button>
                )}
                {detail.status !== 'approved' && (
                  <Button variant="outline" disabled={busy} onClick={() => void download(detail)}>
                    <Download className="mr-2 h-4 w-4" />
                    Datei herunterladen
                  </Button>
                )}
                {detail.status === 'exported' && detail.ebicsReady && (
                  <Button disabled={busy} onClick={() => setConfirmation({ kind: 'transmit', run: detail })}>
                    <Network className="mr-2 h-4 w-4" />
                    Per EBICS übertragen
                  </Button>
                )}
                {detail.status === 'exported' && !detail.ebicsReady && (
                  <Button disabled={busy} onClick={() => setConfirmation({ kind: 'upload', run: detail })}>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload bestätigen
                  </Button>
                )}
              </div>
            )}

            {canEdit && (detail.status === 'draft' || detail.status === 'approved') && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  className="text-destructive"
                  disabled={busy}
                  onClick={() => setConfirmation({ kind: 'cancel', run: detail })}
                >
                  <Ban className="mr-2 h-4 w-4" />
                  Lauf abbrechen
                </Button>
                {detail.status === 'draft' ? (
                  <Button disabled={busy} onClick={() => void act(() => billingApi.approveRun(detail.id), 'Lauf freigegeben')}>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {isFileRun(detail) && detail.direction === 'debit' ? 'Freigeben und Zahler informieren' : 'Freigeben'}
                  </Button>
                ) : isFileRun(detail) ? null : (
                  <Button disabled={busy} onClick={() => setConfirmation({ kind: 'submit', run: detail })}>
                    <Send className="mr-2 h-4 w-4" />
                    Einreichen
                  </Button>
                )}
              </div>
            )}

            <div className="max-h-[55vh] overflow-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Auftrag</TableHead>
                    <TableHead>{detail.direction === 'credit' ? 'Empfänger' : 'Zahler'}</TableHead>
                    {detail.direction === 'debit' && <TableHead>Schüler</TableHead>}
                    <TableHead>Verwendungszweck</TableHead>
                    <TableHead className="text-right">Betrag</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="text-sm tabular-nums">
                        {order.endToEndId}
                        {(order.sequenceType || order.preNotifiedAt) && (
                          <div className="flex gap-2 text-xs text-muted-foreground">
                            {order.sequenceType && <span>{order.sequenceType === 'FRST' ? 'Erstlastschrift' : 'Folgelastschrift'}</span>}
                            {order.preNotifiedAt && (
                              <span title={`Vorabinformation ${formatIsoDate(order.preNotifiedAt)}`}>
                                <Mail className="inline h-3 w-3" /> informiert
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{order.payerName ?? order.debtorName ?? '–'}</div>
                        <div className="text-xs text-muted-foreground">Debitor {order.debtorNumber ?? '–'}</div>
                      </TableCell>
                      {detail.direction === 'debit' && (
                        <TableCell className="text-sm">
                          {order.studentNames?.length ? order.studentNames.join(', ') : '–'}
                        </TableCell>
                      )}
                      <TableCell className="max-w-xs truncate text-sm" title={order.remittanceText}>
                        {order.remittanceText}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatCents(order.amountCents)}</TableCell>
                      <TableCell>
                        <Badge variant={ORDER_STATUS[order.status]?.variant ?? 'outline'} size="sm">
                          {ORDER_STATUS[order.status]?.label ?? order.status}
                          {order.returnReasonCode ? ` · ${order.returnReasonCode}` : ''}
                        </Badge>
                        {order.failureReason && (
                          <div className="mt-1 text-xs text-destructive">{order.failureReason}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {canCapture && order.status === 'submitted' && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() =>
                              void act(() => billingApi.settleOrder(order.id), 'Zahlungseingang erfasst')
                            }
                          >
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Zahlung erfassen
                          </Button>
                        )}
                        {canReturn && order.status === 'settled' && (
                          <Button variant="outline" size="sm" disabled={busy} onClick={() => setReturning(order)}>
                            <Undo2 className="mr-2 h-4 w-4" />
                            Rücklastschrift
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {confirmation && (
        <Dialog open onOpenChange={(open) => !open && setConfirmation(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {confirmation.kind === 'submit'
                  ? 'Zahlungslauf einreichen?'
                  : confirmation.kind === 'upload'
                    ? 'Upload bei der Bank bestätigen?'
                    : confirmation.kind === 'transmit'
                      ? 'Per EBICS an die Bank übertragen?'
                      : 'Zahlungslauf abbrechen?'}
              </DialogTitle>
              <DialogDescription>
                {confirmation.kind === 'transmit'
                  ? `${confirmation.run.itemCount} Aufträge über ${formatCents(confirmation.run.totalCents)} gehen jetzt an die Bank. Ausgeführt wird erst nach der Freigabe in der Banking-App; am ${formatIsoDate(confirmation.run.collectionDate)} wird der Lauf als bezahlt gebucht, sofern die Bank ihn nicht ablehnt.`
                  : confirmation.kind === 'upload'
                  ? `Nur bestätigen, wenn die Datei ${confirmation.run.fileName ?? ''} im Onlinebanking hochgeladen und freigegeben ist. Danach lässt sich der Lauf nicht mehr abbrechen; am ${formatIsoDate(confirmation.run.collectionDate)} wird er als bezahlt gebucht.`
                  : confirmation.kind === 'submit'
                  ? confirmation.run.provider === 'stripe'
                    ? `Stripe zieht ${formatCents(confirmation.run.totalCents)} sofort von ${confirmation.run.itemCount} Zahlern ein. Das lässt sich nicht zurücknehmen.`
                    : `${confirmation.run.itemCount} Zahlungsaufforderungen über ${formatCents(confirmation.run.totalCents)} werden als eingereicht markiert.`
                  : 'Die Posten werden wieder frei und können in einen neuen Lauf aufgenommen werden.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmation(null)} disabled={busy}>
                Zurück
              </Button>
              <Button
                variant={confirmation.kind === 'cancel' ? 'destructive' : 'default'}
                disabled={busy}
                onClick={() =>
                  confirmation.kind === 'submit'
                    ? void act(() => billingApi.submitRun(confirmation.run.id), 'Lauf eingereicht')
                    : confirmation.kind === 'upload'
                      ? void act(() => billingApi.confirmUpload(confirmation.run.id), 'Upload bestätigt')
                      : confirmation.kind === 'transmit'
                        ? void act(() => billingApi.transmitRun(confirmation.run.id), 'An die Bank übertragen')
                        : void act(() => billingApi.cancelRun(confirmation.run.id), 'Lauf abgebrochen')
                }
              >
                {confirmation.kind === 'submit'
                  ? 'Jetzt einreichen'
                  : confirmation.kind === 'upload'
                    ? 'Upload bestätigen'
                    : confirmation.kind === 'transmit'
                      ? 'Jetzt übertragen'
                      : 'Lauf abbrechen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {returning && (
        <ReturnDialog
          order={returning}
          busy={busy}
          onClose={() => setReturning(null)}
          onSubmit={async (input) => {
            await act(() => billingApi.returnOrder(returning.id, input), 'Rücklastschrift erfasst');
            setReturning(null);
          }}
        />
      )}
    </div>
  );
}

function ChargeFeesDialog({ onClose }: { onClose: () => void }) {
  const [through, setThrough] = useState(todayIso());
  const [saving, setSaving] = useState(false);

  const run = async () => {
    setSaving(true);
    try {
      const summary = await billingApi.runPeriodicCharges({ through });
      toast.success(
        summary.charges > 0
          ? `${summary.charges} Beitr${summary.charges === 1 ? 'ag' : 'äge'} über ${formatCents(summary.totalCents)} gebucht`
          : 'Keine fälligen Beiträge offen',
      );
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Beiträge konnten nicht gebucht werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Beiträge buchen</DialogTitle>
          <DialogDescription>
            Bucht für alle Beitragsverträge die Forderungen, die bis zum Stichtag fällig sind. Bereits gebuchte
            Zeiträume werden nicht doppelt gebucht. Das passiert auch nachts automatisch.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="fees-through">Fällig bis</Label>
          <Input id="fees-through" type="date" value={through} onChange={(event) => setThrough(event.target.value)} />
          <p className="text-xs text-muted-foreground">
            Ein späterer Stichtag bucht z. B. den Beitrag für den nächsten Monat schon vor dem Zahlungslauf.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void run()} disabled={saving || !through}>
            {saving ? 'Bucht …' : 'Beiträge buchen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CreateRunDialogProps {
  list: PaymentRunList;
  onClose: () => void;
  onCreated: (run: PaymentRunDetail) => Promise<void>;
}

function CreateRunDialog({ list, onClose, onCreated }: CreateRunDialogProps) {
  const activeEntities = list.entities.filter((entity) => entity.isActive);
  const [entityId, setEntityId] = useState(activeEntities.length === 1 ? String(activeEntities[0].id) : '');
  const [provider, setProvider] = useState(list.providers.includes('invoice') ? 'invoice' : (list.providers[0] ?? ''));
  const [collectionDate, setCollectionDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      const run = await billingApi.createRun({ billingEntityId: Number(entityId), provider, collectionDate });
      toast.success(`Entwurf ${run.runNumber} mit ${run.itemCount} Aufträgen angelegt`);
      await onCreated(run);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Zahlungslauf konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Zahlungslauf anlegen</DialogTitle>
          <DialogDescription>
            Nimmt alle bis zum Einzugsdatum fälligen offenen Posten auf, deren Vertragskonto dieses
            Zahlungsmittel nutzt. Gesperrte Konten und bereits beauftragte Posten bleiben draußen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="run-entity">Abrechnende Einheit</Label>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger id="run-entity">
                <SelectValue placeholder="Auswählen" />
              </SelectTrigger>
              <SelectContent>
                {activeEntities.map((entity) => (
                  <SelectItem key={entity.id} value={String(entity.id)}>
                    {entity.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="run-provider">Zahlungsanbieter</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger id="run-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {list.providers.map((key) => (
                  <SelectItem key={key} value={key}>
                    {PROVIDER_LABEL[key] ?? key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="run-date">Einzugsdatum</Label>
            <Input
              id="run-date"
              type="date"
              value={collectionDate}
              onChange={(event) => setCollectionDate(event.target.value)}
            />
            {provider === HAUSBANK && (
              <p className="text-xs text-muted-foreground">
                Bankarbeitstag nach Ablauf der Vorabinformation (je Einheit eingestellt) – die Zahler werden bei
                der Freigabe informiert.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void create()} disabled={saving || !entityId || !provider || !collectionDate}>
            {saving ? 'Legt an …' : 'Entwurf anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ReturnDialogProps {
  order: PaymentOrder;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: { reasonCode: string; feeCents?: number; bookedOn?: string }) => Promise<void>;
}

function ReturnDialog({ order, busy, onClose, onSubmit }: ReturnDialogProps) {
  const [reasonCode, setReasonCode] = useState('AM04');
  const [fee, setFee] = useState('');
  const [bookedOn, setBookedOn] = useState(todayIso());

  const submit = () => {
    const feeCents = fee.trim() === '' ? 0 : parseEuroToCents(fee);
    if (feeCents === null) {
      toast.error('Gebühr als Betrag angeben, z. B. 3,50');
      return;
    }
    void onSubmit({ reasonCode, feeCents, bookedOn });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rücklastschrift erfassen</DialogTitle>
          <DialogDescription>
            {order.debtorName ?? 'Zahler'} · {formatCents(order.amountCents)}. Die ursprünglichen Forderungen sind
            danach wieder offen; eine Gebühr wird zusätzlich als Forderung gebucht.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="return-reason">Rückgabegrund</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger id="return-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RETURN_REASONS.map((reason) => (
                  <SelectItem key={reason.code} value={reason.code}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="return-fee">Bankgebühr (€)</Label>
              <Input
                id="return-fee"
                inputMode="decimal"
                placeholder="0,00"
                value={fee}
                onChange={(event) => setFee(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="return-date">Gebucht am</Label>
              <Input id="return-date" type="date" value={bookedOn} onChange={(event) => setBookedOn(event.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant="destructive" onClick={submit} disabled={busy}>
            Rücklastschrift buchen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RefundsDialogProps {
  list: PaymentRunList;
  onClose: () => void;
  onRunCreated: (run: PaymentRunDetail) => Promise<void>;
}

function RefundsDialog({ list, onClose, onRunCreated }: RefundsDialogProps) {
  const [refunds, setRefunds] = useState<RefundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [executionDate, setExecutionDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const entityName = new Map(list.entities.map((entity) => [entity.id, entity.name]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await billingApi.listRefunds();
      setRefunds(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erstattungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openByEntity = useMemo(() => {
    const map = new Map<number, RefundRequest[]>();
    for (const refund of refunds.filter((item) => item.status === 'open')) {
      map.set(refund.billingEntityId, [...(map.get(refund.billingEntityId) ?? []), refund]);
    }
    return map;
  }, [refunds]);

  const createRun = async (billingEntityId: number) => {
    setBusy(true);
    try {
      const run = await billingApi.createRefundRun({ billingEntityId, executionDate });
      toast.success(`Überweisungslauf ${run.runNumber} angelegt`);
      await onRunCreated(run);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Überweisungslauf konnte nicht angelegt werden');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (refund: RefundRequest) => {
    setBusy(true);
    try {
      await billingApi.cancelRefund(refund.id);
      toast.success('Erstattung abgebrochen, das Guthaben ist wieder offen');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Abbrechen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Erstattungen</DialogTitle>
          <DialogDescription>
            Erstattungen von Guthaben legt die Buchhaltung am Vertragskonto an. Offene Erstattungen einer Einheit
            gehen gesammelt als Überweisungslauf (pain.001) an die Bank.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <LoadingState message="Erstattungen werden geladen …" />
        ) : (
          <div className="space-y-4">
            {openByEntity.size > 0 && (
              <div className="flex flex-wrap items-end gap-3 rounded-lg border p-3">
                <div className="space-y-2">
                  <Label htmlFor="refund-execution">Ausführung am</Label>
                  <Input
                    id="refund-execution"
                    type="date"
                    value={executionDate}
                    onChange={(event) => setExecutionDate(event.target.value)}
                  />
                </div>
                {[...openByEntity.entries()].map(([entityId, items]) => (
                  <Button key={entityId} disabled={busy || !executionDate} onClick={() => void createRun(entityId)}>
                    <Plus className="mr-2 h-4 w-4" />
                    {entityName.get(entityId) ?? `Einheit #${entityId}`}: {items.length} überweisen (
                    {formatCents(items.reduce((sum, item) => sum + item.amountCents, 0))})
                  </Button>
                ))}
              </div>
            )}
            {refunds.length === 0 ? (
              <EmptyState message="Keine Erstattungen." />
            ) : (
              <div className="max-h-[50vh] overflow-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empfänger</TableHead>
                      <TableHead>Einheit</TableHead>
                      <TableHead>Verwendungszweck</TableHead>
                      <TableHead className="text-right">Betrag</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {refunds.map((refund) => (
                      <TableRow key={refund.id}>
                        <TableCell>
                          <div className="font-medium">{refund.recipientName}</div>
                          <div className="text-xs text-muted-foreground">{refund.ibanMasked}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {entityName.get(refund.billingEntityId) ?? `#${refund.billingEntityId}`}
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm" title={refund.remittanceText}>
                          {refund.remittanceText}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatCents(refund.amountCents)}</TableCell>
                        <TableCell>
                          <Badge variant={REFUND_STATUS[refund.status]?.variant ?? 'outline'} size="sm">
                            {REFUND_STATUS[refund.status]?.label ?? refund.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {refund.status === 'open' && (
                            <Button variant="outline" size="sm" disabled={busy} onClick={() => void cancel(refund)}>
                              <Ban className="mr-2 h-4 w-4" />
                              Abbrechen
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
